import { Link, Outlet, useBlocker, useRouterState } from '@tanstack/react-router'
import {
  AlertTriangle,
  BellRing,
  Building2,
  CalendarClock,
  Check,
  Hospital,
  Network,
  RefreshCw,
  Stethoscope,
  Tag,
  Users,
} from 'lucide-react'
import { relTime } from '../model/dates'
import { SETTINGS_COPY } from '../model/messages'
import { needsRecipients, PF_MESSAGE_COPY } from '../model/pfMessages'
import { useSettingsDraft } from '../SettingsDraftProvider'
import { ConflictDialog, LeavePrompt } from './SettingsDialogs'

type Area = 'appointment' | 'profile' | 'sources' | 'notifications'
type SubTab = 'map' | 'visit-types' | 'locations' | 'providers' | 'rules'

/** Appointment settings sub-tabs; `/settings` opens the Booking map (Frontend §6.1). */
const SUB_TABS = [
  { id: 'map', label: 'Booking map', icon: Network, to: '/clinics/$clinicId/settings/booking/map' },
  { id: 'visit-types', label: 'Visit types', icon: Stethoscope, to: '/clinics/$clinicId/settings/booking/visit-types' },
  { id: 'locations', label: 'Locations', icon: Building2, to: '/clinics/$clinicId/settings/booking/locations' },
  { id: 'providers', label: 'Providers', icon: Users, to: '/clinics/$clinicId/settings/booking/providers' },
  { id: 'rules', label: 'Booking rules', icon: CalendarClock, to: '/clinics/$clinicId/settings/booking/rules' },
] as const

function where(pathname: string): { area: Area; sub: SubTab | undefined } {
  if (pathname.endsWith('/settings/profile')) return { area: 'profile', sub: undefined }
  if (pathname.endsWith('/settings/sources')) return { area: 'sources', sub: undefined }
  if (pathname.endsWith('/settings/notifications')) return { area: 'notifications', sub: undefined }
  const sub = SUB_TABS.find((t) => pathname.endsWith(`/settings/booking/${t.id}`))?.id
  return { area: 'appointment', sub }
}

/**
 * C-04, the Settings layout (UserFlows C-04, Frontend §6.6): page copy per area, Refresh from PF with "Last synced
 * with PF", the Appointment settings · Clinic profile · Sources tabs (links, one route each) and, while a super admin
 * has it on, task 24's "PF internal message notification" tab (C-14), the sub-tabs, the
 * warnings box and the save bar. The draft (provider above) survives switching tabs; Sources saves on its own.
 * Task 09: the save conflict dialog (D-34, D-60) and the leave prompt (Architecture P-07): in-app navigation out of
 * this clinic's `/settings/*` while dirty asks first, and so does reloading or closing the tab (`beforeunload`).
 */
export function SettingsLayout() {
  const s = useSettingsDraft()
  const pathname = useRouterState({ select: (st) => st.location.pathname })
  const { area, sub } = where(pathname)
  const params = { clinicId: s.clinicId }
  const lastSynced = s.base.lastSyncedAt
  const settingsPrefix = `/clinics/${s.clinicId}/settings`
  const blocker = useBlocker({
    shouldBlockFn: ({ next }) =>
      s.dirty && next.pathname !== settingsPrefix && !next.pathname.startsWith(`${settingsPrefix}/`),
    enableBeforeUnload: () => s.dirty,
    withResolver: true,
  })

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <p>
            {area === 'appointment'
              ? SETTINGS_COPY.appointment
              : area === 'profile'
                ? SETTINGS_COPY.profile
                : area === 'notifications'
                  ? PF_MESSAGE_COPY.purpose
                  : SETTINGS_COPY.sources}
          </p>
        </div>
        {area === 'appointment' && (
          <div className="actions sync-box">
            <span className="hint">
              {lastSynced ? (
                <>
                  Last synced with PF
                  <br />
                  <b>{relTime(lastSynced)}</b>
                </>
              ) : (
                SETTINGS_COPY.neverSynced
              )}
            </span>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={s.refreshing}
              onClick={() => {
                void s.refresh()
              }}
            >
              <RefreshCw aria-hidden /> Refresh from PF
            </button>
          </div>
        )}
      </div>

      <nav className="top-tabs" aria-label="Settings area">
        <Link
          to="/clinics/$clinicId/settings/booking/map"
          params={params}
          className="top-tab"
          aria-current={area === 'appointment' ? 'page' : undefined}
        >
          <CalendarClock aria-hidden /> Appointment settings
        </Link>
        <Link
          to="/clinics/$clinicId/settings/profile"
          params={params}
          className="top-tab"
          aria-current={area === 'profile' ? 'page' : undefined}
        >
          <Hospital aria-hidden /> Clinic profile
        </Link>
        <Link
          to="/clinics/$clinicId/settings/sources"
          params={params}
          className="top-tab"
          aria-current={area === 'sources' ? 'page' : undefined}
        >
          <Tag aria-hidden /> Sources
        </Link>
        {s.base.pfMessages.enabled && (
          <Link
            to="/clinics/$clinicId/settings/notifications"
            params={params}
            className="top-tab"
            aria-current={area === 'notifications' ? 'page' : undefined}
          >
            <BellRing aria-hidden /> {PF_MESSAGE_COPY.tab}
          </Link>
        )}
      </nav>

      {area === 'appointment' && (
        <nav className="tabs" aria-label="Appointment settings">
          {SUB_TABS.map((t) => (
            <Link key={t.id} to={t.to} params={params} className="tab" aria-current={t.id === sub ? 'page' : undefined}>
              <t.icon aria-hidden /> {t.label}
            </Link>
          ))}
        </nav>
      )}

      {area === 'appointment' && s.warnings.length > 0 && (
        <div className="warn-box" role="note" aria-label="Warnings">
          <AlertTriangle aria-hidden />
          <div>
            {s.warnings.map((w) => (
              <p key={w}>{w}</p>
            ))}
          </div>
        </div>
      )}

      {/* Task 24: C-14's red warning (on C-14 itself and in the warnings above on Appointment settings). */}
      {(area === 'profile' || area === 'sources') && needsRecipients(s.draft) && (
        <div className="warn-box is-danger" role="note" aria-label="PF internal message notification">
          <AlertTriangle aria-hidden />
          <div>
            <p>{PF_MESSAGE_COPY.noRecipients}</p>
          </div>
        </div>
      )}

      <Outlet />

      {s.dirty && (
        <div className="save-bar" role="region" aria-label="Unsaved changes">
          <div className="grow">
            <b>{SETTINGS_COPY.unsaved}</b>
            {s.problems.length > 0 ? (
              <span className="err">
                {s.problems[0]}
                {s.problems.length > 1 ? ` (+${String(s.problems.length - 1)} more)` : ''}
              </span>
            ) : (
              <span>{SETTINGS_COPY.patientsSee}</span>
            )}
          </div>
          <button type="button" className="btn btn-ghost" onClick={s.discard}>
            Discard
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={s.problems.length > 0 || s.saving}
            onClick={() => {
              void s.save()
            }}
          >
            <Check aria-hidden /> Save changes
          </button>
        </div>
      )}

      {s.conflict && (
        <ConflictDialog
          conflict={s.conflict}
          onReload={() => {
            void s.reloadLatest()
          }}
          onCancel={s.dismissConflict}
        />
      )}
      {blocker.status === 'blocked' && <LeavePrompt onStay={blocker.reset} onLeave={blocker.proceed} />}
    </>
  )
}
